import {
  createContext,
  ReactElement,
  ReactNode,
  useContext,
  useLayoutEffect,
  useMemo,
  useState,
} from 'react'
import { createPortal } from 'react-dom'
import { Box, Container } from '@mui/material'
import { usePlatform } from '@/hooks'

type HeaderSlotValue = {
  node: HTMLElement | null
  setNode: (node: HTMLElement | null) => void
}

const HeaderSlotContext = createContext<HeaderSlotValue | null>(null)

export function useHeaderSlot() {
  return useContext(HeaderSlotContext)
}

export function HeaderSlotProvider({ children }: { children: ReactNode }) {
  const [node, setNode] = useState<HTMLElement | null>(null)
  const value = useMemo(() => ({ node, setNode }), [node])

  return (
    <HeaderSlotContext.Provider value={value}>
      {children}
    </HeaderSlotContext.Provider>
  )
}

export function HeaderBar() {
  const slot = useHeaderSlot()
  const { isAndroid } = usePlatform()
  const [filled, setFilled] = useState(false)
  const node = slot?.node ?? null

  useLayoutEffect(() => {
    if (!node) return
    const update = () => setFilled(node.childElementCount > 0)
    update()
    const observer = new MutationObserver(update)
    observer.observe(node, { childList: true })
    return () => observer.disconnect()
  }, [node])

  if (!slot) return null

  return (
    <Box
      sx={{
        display: filled ? 'block' : 'none',
        flexShrink: 0,
        borderBottom: 1,
        borderColor: 'divider',
        bgcolor: 'background.paper',
        pt: isAndroid ? 6 : 'env(safe-area-inset-top, 0px)',
      }}
    >
      <Container maxWidth="md" ref={slot.setNode} />
    </Box>
  )
}

export function HeaderPortal({ children }: { children: ReactNode }) {
  const slot = useHeaderSlot()
  if (!slot) return children
  if (!slot.node) return null
  return createPortal(children, slot.node) as unknown as ReactElement
}
